import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, shareReplay, switchMap, tap } from 'rxjs';
import { UsuarioSession } from '../core/models/usuarioSession.model';
import { environment } from '../../environments/environment';
import { TipoUsuario } from '../core/enums/tipoUsuario.enum';

@Injectable({ providedIn: 'root' })
export class SessionService {

  private inicializacao$?: Observable<UsuarioSession>;

  private baseUrl = environment.apiUrl;

  private readonly TOKEN_KEY = 'auth_token';
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.hasToken());

  private usuarioSubject = new BehaviorSubject<UsuarioSession | null>(null);
  usuario$ = this.usuarioSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
    this.isAuthenticatedSubject.next(true);
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  hasToken(): boolean {
    return !!this.getToken();
  }

  isAuthenticated(): Observable<boolean> {
    return this.isAuthenticatedSubject.asObservable();
  }

  decodeToken(): any {
    const token = this.getToken();
    if (!token) return null;

    try {
      const payload = token.split('.')[1];
      return JSON.parse(atob(payload));
    } catch {
      return null;
    }
  }

  isTokenExpired(): boolean {
    const decoded = this.decodeToken();
    if (!decoded?.exp) return true;
    return new Date(decoded.exp * 1000) < new Date();
  }

  removeToken(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    this.isAuthenticatedSubject.next(false);
  }

  isSessionValid(): boolean {
    return this.hasToken() && !this.isTokenExpired();
  }

  checkSessionStatus() {
    return this.http.get(`${this.baseUrl}/api/auth/validate`, { withCredentials: true });
  }

    logout() {
      this.http.post(`${this.baseUrl}/api/auth/logout`, {}, { withCredentials: true }).subscribe({
        next: () => {
          this.resetSessao();
          this.router.navigate(['/login']);
        },
        error: () => {
          this.resetSessao();
          this.router.navigate(['/login']);
        }
      });
    }

    private resetSessao() {
      localStorage.removeItem(this.TOKEN_KEY);
      this.isAuthenticatedSubject.next(false);
      this.usuarioSubject.next(null);
      this.inicializacao$ = undefined; // 🔥 LIMPA O CACHE DO shareReplay
    }


  setUsuario(usuario: UsuarioSession) {
    this.usuarioSubject.next(usuario);
  }

  getUsuario(): UsuarioSession | null {
    return this.usuarioSubject.value;
  }

  carregarUsuarioLogado(): Observable<UsuarioSession> {
    // console.log(`${this.baseUrl}/usuarios`);
    return this.http.get<UsuarioSession>(`${this.baseUrl}/usuarios`, { withCredentials: true });
  }

  inicializarSessao(force = false): Observable<UsuarioSession> {
    if (force || !this.inicializacao$) {
      this.inicializacao$ = this.checkSessionStatus().pipe(
        switchMap(() => this.carregarUsuarioLogado()),
        tap(usuario => this.setUsuario(usuario)),
        shareReplay(1)
      );
    }
    return this.inicializacao$;
    }

    isFornecedorBasico(): boolean {
      const usuario = this.getUsuario();
      if (!usuario) return false;

      return (
        (usuario.isnUsuarioTipo === TipoUsuario.FPF ||
        usuario.isnUsuarioTipo === TipoUsuario.FPJ) &&
        (usuario.isnStatusValidacaoFornecedor === 1 ||
        usuario.isnStatusValidacaoFornecedor == null)
      );
    }

    isFornecedorCompleto(): boolean {
      const usuario = this.getUsuario();
      if (!usuario) return false;

      return (
        (usuario.isnUsuarioTipo === TipoUsuario.FPF ||
        usuario.isnUsuarioTipo === TipoUsuario.FPJ) &&
        !!usuario.isnFornecedor &&
        usuario.isnStatusValidacaoFornecedor !== 1 &&
        usuario.isnStatusValidacaoFornecedor != null
      );
    }

    isFornecedorValido(): boolean {
      const usuario = this.getUsuario();
      if (!usuario) return false;

      return (
        !!usuario.isnFornecedor &&
        usuario.isnStatusValidacaoFornecedor !== 1 &&
        usuario.isnStatusValidacaoFornecedor != null
      );
    }

    getDashboardInicial(): string {
      const usuario = this.getUsuario();
      if (!usuario) return '/login';
      const { isnUsuarioTipo, isnStatusValidacaoFornecedor } = usuario;
      if (
        isnUsuarioTipo === TipoUsuario.FPF ||
        isnUsuarioTipo === TipoUsuario.FPJ
      ) {
        if (isnStatusValidacaoFornecedor === 1 || isnStatusValidacaoFornecedor == null) {
          return '/pages/dashboard';
        }
        return '/pages/dashboardFornecedor';
      }

      switch (isnUsuarioTipo) {
        case TipoUsuario.ADA:
          return '/pages/dashboardAreaInteresse';
        case TipoUsuario.ADM:
          return '/pages/dashboardADM';
        case TipoUsuario.ADV:
          return '/pages/dashboardValidador';
        default:
          return '/login';
      }
    }
}
